import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { StartPage } from '../../BeFi20/Survey/components/StartPage/StartPage';
import { Progress } from '../../CommonComponents/OPL/Progress/Progress';
import { CardSorting } from './components/CardSorting/CardSorting';
import { STEPS } from './Workflow.meta';
import { StickyFooter } from '../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { PulseCheckVerticalLogo } from '../img/PulseCheckVerticalLogo';
import { WorkflowProvider } from './context/WorkflowProvider';
import { useWorkflowContext } from './context/useWorkflowContext';
import { CategoryType, PulseCheckWorkflow, useWorkflowQuery } from '../api/useWorkflowQuery';
import { StartPageLoading } from '../../BeFi20/Survey/components/StartPage/StartPage.loading';
import { ErrorState } from '../../BeFi20/components/ErrorState';
import { Fulfillment } from './components/Fulfillment/Fulfillment';
import { SaveCategoryDTO, useSaveWorkflowMutation } from '../api/useSaveWorkflowMutation';
import { toast } from '../../CommonComponents/OPL/Toast/Toast';
import { MESSAGES } from '../../CommonConstants';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { OnboardingWorkflow } from '../../CommonSelectors/CommonArea.types';
import {
  isConsumerArea as selectIsConsumerArea,
  selectOnboardingWorkflow,
} from '../../CommonSelectors/CommonAreaSelector';
import { useBootstrapConsumerArea } from '../../ConsumerArea/hooks/useBootstrapConsumerArea';
import { FullPageLoading } from '../../CommonComponents/OPL/FullpageLoading/FullPageLoading';

interface Props {
  clientId: string;
}

export const Workflow = ({ clientId }: Props) => {
  const { isLoading, isError, refetch, data } = useWorkflowQuery({ clientId });

  if (isLoading) {
    return <StartPageLoading logo={<PulseCheckVerticalLogo />} />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} />;
  }

  if (!data) {
    throw new Error('No data in PulseCheck workflow');
  }

  return <WorkflowContent clientId={clientId} data={data} />;
};

interface ContentProps {
  clientId: string;
  data: PulseCheckWorkflow;
}

const WorkflowContent = ({ clientId, data }: ContentProps) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [categories, setCategories] = useState<CategoryType[]>(data.categories);
  const [showCategoryNumbers, setShowCategoryNumbers] = useState(data.isRetakingAssessment);

  const isConsumerArea = useSelector(selectIsConsumerArea);
  const onboardingWorkflow = useSelector(selectOnboardingWorkflow);
  const {
    mutateAsync: bootstrapConsumerArea,
    isLoading: isBootStrappingConsumerArea,
  } = useBootstrapConsumerArea();

  const { mutateAsync: saveWorkflow, isLoading: isSavingWorkflow } = useSaveWorkflowMutation();

  const handleSaveWorkflow = async () => {
    try {
      const parsedCategories: SaveCategoryDTO[] = categories.map(({ id, fulfillment }, index) => ({
        id,
        fulfillmentValue: fulfillment,
        orderNumber: index,
      }));

      await saveWorkflow({ clientId, categories: parsedCategories });

      if (isConsumerArea && onboardingWorkflow !== OnboardingWorkflow.None) {
        await bootstrapConsumerArea({
          clientId: clientId.toString(),
          completedWorkflow: true,
        });
      }

      ADV.util.Router.go2('PulseCheckResults', { clientId });
    } catch (error) {
      console.error(error);
      toast.error(MESSAGES.GENERIC_ERROR);
    }
  };

  return (
    <>
      <ScrollHereOnce key={stepIndex} />

      <Progress value={(stepIndex / STEPS.length) * 100} isLarge isFlat css="flex-shrink: 0" />

      {isBootStrappingConsumerArea && <FullPageLoading />}

      <WorkflowProvider
        value={{
          data,
          categories,
          setCategories,
          stepIndex,
          setStepIndex,
          showCategoryNumbers,
          setShowCategoryNumbers,
        }}
      >
        <StepContent />
      </WorkflowProvider>

      {stepIndex > 0 && (
        <StickyFooter css="width: 100%; min-height: unset">
          <Button
            variant="secondary"
            leftIcon="fas fa-chevron-left"
            size="large"
            onClick={() => setStepIndex(stepIndex - 1)}
            mr="auto"
          >
            Previous
          </Button>

          <Button
            type="submit"
            rightIcon={stepIndex < STEPS.length - 1 ? 'fas fa-chevron-right' : undefined}
            size="large"
            isLoading={isSavingWorkflow}
            onClick={() => {
              if (stepIndex === STEPS.length - 1) {
                handleSaveWorkflow();
                return;
              }

              setStepIndex(stepIndex + 1);
            }}
          >
            {stepIndex === STEPS.length - 1 ? 'Get My Result' : 'Next'}
          </Button>
        </StickyFooter>
      )}
    </>
  );
};

const StepContent = () => {
  const { stepIndex, setStepIndex } = useWorkflowContext();

  if (stepIndex === 0) {
    return (
      <StartPage
        title="True Wealth is More Than Money"
        logo={<PulseCheckVerticalLogo />}
        description="Discover the shape your fulfillment takes and how your financial plan can help you actualize it."
        helperText="Answering these questions will take about 2 minutes."
        ctaText="Start the Assessment"
        onStartSurvey={() => setStepIndex(1)}
      />
    );
  }

  if (stepIndex === 1) {
    return <CardSorting />;
  }

  if (stepIndex === 2) {
    return <Fulfillment />;
  }

  throw new Error(`Invalid step index: ${stepIndex}`);
};
